import { FC, useState } from "react";
import { Button } from "@altimateai/lego";
import { IconShare3 } from "@tabler/icons-react";
import ShareDialog from "./ShareDialog";

interface StudioShareButtonProps {
  sessionId?: string;
  sessionName: string;
}

/**
 * Studio-specific Share button rendered at the bottom of every assistant
 * message in the Studio chat (via the `components.shareButton` slot on
 * `ChatProvider`). Visually identical to the default `ShareMessage` from
 * `@altimateai/ui-components/chatbotV2`, but on click opens the full
 * `ShareDialog` (Link / Slack / Email tabs) instead of the legacy
 * email-only `EmailDialog`.
 *
 * AI-6343 — consolidates the two Share entry points (the icon-only one
 * in the page header AND the bottom-of-message pill) onto a single
 * dialog so users have one consistent way to share a Studio session.
 */
const StudioShareButton: FC<StudioShareButtonProps> = ({ sessionId, sessionName }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="al-bg-transparent al-text-gray-500"
        onClick={() => setOpen(true)}
        disabled={!sessionId}
        aria-label="Share session"
      >
        <IconShare3 aria-hidden="true" />
        Share
      </Button>
      {sessionId && (
        <ShareDialog
          sessionId={sessionId}
          sessionName={sessionName}
          open={open}
          onOpenChange={setOpen}
        />
      )}
    </>
  );
};

export default StudioShareButton;
